/**
 * declineInvitation — RPC contract for the callable that turns an
 * invitation down.
 *
 * `invite.entity.ts` marks `decline` `.requiresHandler('declineInvitation')`
 * for the reason acceptInvitation.action.ts gives: the authority is being
 * the addressee, and the caller holds no grant at the organization. The
 * handler flips every still-pending invite naming the proven address at
 * that organization to `declined` inside one transaction and deletes the
 * announcement. No grant is written and none is deleted, because none
 * existed: refusing an offer is not leaving an organization.
 *
 * Fields justified:
 *   orgId — the organization whose invitation is being declined; a refusal
 *           of one says nothing about another.
 *
 * Output: `{ ok: true }`. A second decline is the same answer twice and
 * answers the same, writing nothing.
 *
 * Errors: as acceptInvitation.action.ts — `unauthenticated`,
 * `failed-precondition / email-not-verified`, and
 * `failed-precondition / <InvitationRefusal>` for an invite that is no
 * longer pending (`invite-declined` is never thrown here — see Output).
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export { InvitationRefusal } from "./acceptInvitation.action.ts";

export const DeclineInvitationInput = z
	.object({
		orgId: Id.describe("the organization whose invitation is being declined"),
	})
	.strict();
export type DeclineInvitationInput = z.infer<typeof DeclineInvitationInput>;

export const DeclineInvitationOutput = z
	.object({ ok: z.literal(true) })
	.strict();
export type DeclineInvitationOutput = z.infer<typeof DeclineInvitationOutput>;
